import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { SuiErrorText } from '@splashthat/sui';
import noop from 'lodash/noop';

import { getFormNode } from 'common/entities/formNode/selectors';
import { getFormErrors } from 'common/compounds/FormBuilder/selectors';
import { EMPTY_LABEL, ALL_ERROR_CODES } from '../../../constants/errorCodes';
import { updateFormNodeLabel } from '../../../actions/nodeActions';

import DraftTextEditorWrapper from './DraftTextEditorWrapper';
import disableWhenLocked from './disableWhenLocked';

const DEFAULT_LABEL = 'Label';
const LABEL_EMPTY_ERROR = "Label can't be empty.";
export const INPUT_PLACEHOLDER = 'This will appear on your form.';

const errorMessages = {
    [EMPTY_LABEL]: LABEL_EMPTY_ERROR,
};

/**
 * The view to edit label for a question.
 */
const RichTextLabelControl = ({
    errors,
    value,
    defaultValue,
    onChange,
    label,
    labelMarkup,
    disableBold,
    disableLink,
    disabled,
}) => {
    const labelErrorMessages = errors.map((error) => errorMessages[error]).filter(Boolean);

    const handleChangeLabel = (event, newLabel, newLabelMarkup) => {
        if (newLabel !== label) {
            onChange(newLabel, newLabelMarkup);
        }
    };

    return (
        <React.Fragment>
            <DraftTextEditorWrapper
                fullWidth
                label={label}
                variation={labelErrorMessages.length ? 'error' : ''}
                value={labelMarkup || value}
                placeholder={INPUT_PLACEHOLDER}
                defaultValue={defaultValue}
                onChangeLabel={handleChangeLabel}
                disableBold={disableBold}
                disableLink={disableLink}
                disabled={disabled}
            />
            {labelErrorMessages.map((errorMessage) => (
                <SuiErrorText text={errorMessage} key={errorMessage} />
            ))}
        </React.Fragment>
    );
};

RichTextLabelControl.propTypes = {
    onChange: PropTypes.func.isRequired,
    errors: PropTypes.arrayOf(PropTypes.oneOf([...ALL_ERROR_CODES])).isRequired,
    value: PropTypes.string,
    defaultValue: PropTypes.string,
    label: PropTypes.string,
    labelMarkup: PropTypes.string,
    disableBold: PropTypes.bool,
    disableLink: PropTypes.bool,
    /** disabled is passed when form node editing is locked */
    disabled: PropTypes.bool.isRequired,
};

RichTextLabelControl.defaultProps = {
    value: null,
    labelMarkup: '',
    defaultValue: null,
    label: DEFAULT_LABEL,
    disableBold: false,
    disableLink: false,
};

// -------------------
// Connected component

const mapStateToProps = (state, props) => {
    const formNode = getFormNode(state, props.formNodeId);
    return {
        formNodeId: props.formNodeId,
        value: props.formNodeId ? formNode.label : '',
        labelMarkup: props.formNodeId ? formNode.labelMarkup : '',
        errors: getFormErrors(state),
    };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
    onChange: (label, labelMarkup) => {
        dispatch(updateFormNodeLabel(ownProps.formNodeId, label, labelMarkup));
        ownProps.onChange(label, labelMarkup);
    },
});

const ConnectedRichTextLabelControl = connect(
    mapStateToProps,
    mapDispatchToProps,
)(disableWhenLocked(RichTextLabelControl));

ConnectedRichTextLabelControl.propTypes = {
    formNodeId: PropTypes.string,
    onChange: PropTypes.func,
};
ConnectedRichTextLabelControl.defaultProps = {
    formNodeId: undefined,
    onChange: noop,
};

export default ConnectedRichTextLabelControl;
